import React from 'react';

const CONTACT_EMAIL = 'rain@elevenup.xyz';
const LAST_UPDATED = '10 February 2025';

const TermsOfService: React.FC = () => {
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <nav style={styles.breadcrumb}>
          <a href="/" style={styles.breadcrumbLink}>Home</a>
          <span style={styles.breadcrumbSep}>/</span>
          <span style={styles.breadcrumbCurrent}>Terms of Service</span>
        </nav>

        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.lastUpdated}>Last updated: {LAST_UPDATED}</p>

        {/* ── Plain English Summary ── */}
        <div style={styles.summaryBox}>
          <h2 style={styles.summaryTitle}>The short version</h2>
          <ul style={styles.summaryList}>
            <li>ElevenUp is an educational app for 11+ exam preparation.</li>
            <li>A <strong>parent or guardian</strong> must create the account.</li>
            <li>We have a free tier (2 lessons/day) and an optional paid subscription.</li>
            <li>Our AI tutor helps with learning but <strong>isn't a replacement for a real teacher</strong>.</li>
            <li>We can't guarantee exam results — but we'll do our best to help!</li>
            <li>You can delete your account at any time.</li>
          </ul>
        </div>

        {/* ── Full Terms ── */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. About These Terms</h2>
          <p style={styles.paragraph}>
            These terms of service ("Terms") govern your use of the ElevenUp application and website (the "Service"), operated by 11 Up Company Ltd ("we", "us", "our").
          </p>
          <p style={styles.paragraph}>
            By creating an account or using the Service, you agree to these Terms. If you do not agree, please do not use the Service.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Who Can Use ElevenUp</h2>
          <ul style={styles.list}>
            <li>ElevenUp is designed for children preparing for 11+ grammar school entrance exams in the United Kingdom.</li>
            <li><strong>Accounts must be created by a parent or guardian</strong>, not by children directly.</li>
            <li>By creating an account, you confirm that you are the parent or legal guardian of the child who will use the Service, and that you consent to their use of it.</li>
            <li>We require verifiable parental consent before any child data is processed. Your child's access may be limited until consent is confirmed.</li>
            <li>Guest mode is available without creating an account. Progress in guest mode is stored on the device only.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. The Service</h2>
          <p style={styles.paragraph}>
            ElevenUp provides:
          </p>
          <ul style={styles.list}>
            <li>Practice questions across Maths, English, and Verbal Reasoning</li>
            <li>AI-powered tutoring and lesson generation</li>
            <li>Mock exams simulating real 11+ test conditions</li>
            <li>Progress tracking with streaks, badges, and XP</li>
            <li>Educational games for learning reinforcement</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Free and Paid Plans</h2>

          <h3 style={styles.subTitle}>Free tier</h3>
          <p style={styles.paragraph}>
            The free tier includes limited daily access to lessons and games. Currently this is 2 lessons per day and 1 game per day, though we may adjust these limits.
          </p>

          <h3 style={styles.subTitle}>Premium subscription</h3>
          <ul style={styles.list}>
            <li>Premium unlocks unlimited access to all features.</li>
            <li>Subscriptions are available on a monthly or annual basis.</li>
            <li>New subscribers receive a 7-day free trial. You will not be charged during the trial period.</li>
            <li>Payments are processed by Stripe. We do not see or store your card details.</li>
            <li>You can cancel your subscription at any time. Access continues until the end of your current billing period.</li>
            <li>Refunds are handled on a case-by-case basis. Contact us if you have concerns.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. AI Tutor</h2>
          <p style={styles.paragraph}>
            Our AI tutor "Eli" is powered by third-party AI technology (OpenAI). Please note:
          </p>
          <ul style={styles.list}>
            <li>Eli provides educational assistance only. It is <strong>not a substitute for a qualified teacher, tutor, or educational professional</strong>.</li>
            <li>AI-generated content may occasionally contain errors or inaccuracies. We encourage students to verify important information with a trusted adult.</li>
            <li>Eli is programmed to only discuss 11+ exam subjects and will politely decline off-topic questions.</li>
            <li>All student questions are processed through a content moderation system for safety.</li>
            <li>We do not store conversation history between the student and Eli.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Acceptable Use</h2>
          <p style={styles.paragraph}>
            You agree not to:
          </p>
          <ul style={styles.list}>
            <li>Share your account credentials with others</li>
            <li>Attempt to bypass free tier limits or payment requirements</li>
            <li>Use the AI tutor for any purpose other than educational queries related to 11+ subjects</li>
            <li>Attempt to extract, reverse-engineer, or manipulate the AI tutor's responses</li>
            <li>Submit false question reports or abuse the reporting system</li>
            <li>Use the Service in any way that could harm, disable, or impair it</li>
            <li>Attempt to access other users' data or accounts</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Content and Intellectual Property</h2>
          <ul style={styles.list}>
            <li>All question content, lesson materials, and app design are owned by 11 Up Company Ltd or used under licence.</li>
            <li>You may not copy, reproduce, or distribute our content without permission.</li>
            <li>Question reports submitted by users may be used by us to improve the question bank.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Availability and Updates</h2>
          <ul style={styles.list}>
            <li>We aim to keep ElevenUp available at all times, but cannot guarantee uninterrupted service.</li>
            <li>We may update, modify, or discontinue features at any time.</li>
            <li>For significant changes that affect your subscription, we will provide reasonable notice.</li>
            <li>The app is currently provided as a Progressive Web App (PWA) and may be made available through app stores in the future.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. No Guarantees of Results</h2>
          <p style={styles.paragraph}>
            ElevenUp is a study aid. While we work hard to provide high-quality, exam-relevant content:
          </p>
          <ul style={styles.list}>
            <li>We <strong>cannot guarantee</strong> that using ElevenUp will result in passing any exam.</li>
            <li>Exam formats, content, and scoring vary between schools and regions.</li>
            <li>ElevenUp should be used alongside — not instead of — other preparation methods.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Account Termination</h2>
          <ul style={styles.list}>
            <li><strong>By you:</strong> You can request deletion of your account and all data at any time by contacting us. We will process the request within 30 days.</li>
            <li><strong>By us:</strong> We may suspend or terminate accounts that violate these Terms, particularly for misuse of the AI tutor or attempts to access other users' data. We will notify you if this happens.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Limitation of Liability</h2>
          <p style={styles.paragraph}>
            To the maximum extent permitted by law:
          </p>
          <ul style={styles.list}>
            <li>The Service is provided "as is" without warranties of any kind.</li>
            <li>We are not liable for any indirect, incidental, or consequential damages arising from your use of the Service.</li>
            <li>Our total liability for any claim is limited to the amount you paid us in the 12 months preceding the claim, or £50, whichever is greater.</li>
          </ul>
          <p style={styles.paragraph}>
            Nothing in these Terms excludes or limits our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded by law.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Privacy</h2>
          <p style={styles.paragraph}>
            Your privacy is important to us. Please read our <a href="/privacy" style={styles.link}>Privacy Policy</a> for details on how we collect, use, and protect your data. The Privacy Policy forms part of these Terms.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Changes to These Terms</h2>
          <p style={styles.paragraph}>
            We may update these Terms from time to time. For significant changes, we will notify you by email at least 14 days before the changes take effect. Continued use of the Service after changes take effect constitutes acceptance of the new Terms.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>14. Governing Law</h2>
          <p style={styles.paragraph}>
            These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>15. Contact Us</h2>
          <p style={styles.paragraph}>
            If you have any questions about these Terms, please contact us:
          </p>
          <p style={styles.paragraph}>
            <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a><br />
            <strong>Organisation:</strong> 11 Up Company Ltd
          </p>
        </section>

        <div style={styles.footer}>
          <p style={styles.footerText}>© {new Date().getFullYear()} 11 Up Company Ltd. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f23',
    color: '#e0e0e0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  content: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '40px 24px 80px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#4FCCC6',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '8px 0',
    marginBottom: 24,
  },
  breadcrumb: {
    marginBottom: 24,
    fontSize: 14,
  },
  breadcrumbLink: {
    color: '#4FCCC6',
    textDecoration: 'none',
    fontWeight: 500,
  },
  breadcrumbSep: {
    margin: '0 8px',
    color: '#666',
  },
  breadcrumbCurrent: {
    color: '#999',
  },
  title: {
    fontSize: 36,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 8,
  },
  lastUpdated: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.4)',
    marginBottom: 32,
  },
  summaryBox: {
    backgroundColor: 'rgba(79, 204, 198, 0.08)',
    border: '1px solid rgba(79, 204, 198, 0.2)',
    borderRadius: 16,
    padding: '24px 28px',
    marginBottom: 40,
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#4FCCC6',
    marginTop: 0,
    marginBottom: 16,
  },
  summaryList: {
    margin: 0,
    paddingLeft: 20,
    lineHeight: 1.8,
    fontSize: 15,
    color: 'rgba(255,255,255,0.85)',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: 12,
    marginTop: 0,
  },
  subTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 8,
    marginTop: 16,
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 12,
  },
  list: {
    paddingLeft: 20,
    lineHeight: 1.8,
    fontSize: 15,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 12,
  },
  link: {
    color: '#4FCCC6',
    textDecoration: 'none',
  },
  footer: {
    marginTop: 48,
    paddingTop: 24,
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  footerText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.3)',
    textAlign: 'center' as const,
  },
};

export default TermsOfService;
